import React, { useRef, useState, useEffect, useLayoutEffect } from 'react'
import { usePlatform } from '../../../../hooks/usePlatform'
import { CURVE_WIDTH, R_SEG, L_SEG } from '../tabBarConstants'
import type { ActiveTabShapeProps } from './ActiveTabShape.types'

const ActiveTabShapeComponent: React.FC<ActiveTabShapeProps> = ({ isFirstTab }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })
  const { isMac } = usePlatform()
  // On Windows, extend fill 1px below to cover the content area's top border
  const borderExt = isMac ? 0 : 1

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    setSize({ w: el.offsetWidth, h: el.offsetHeight })
  }, [isFirstTab])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        setSize({ w: Math.round(width), h: Math.round(height) })
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const CW = CURVE_WIDTH
  const totalW = size.w
  const H = size.h

  const shapeClass = `tab-bar__tab-shape${isFirstTab ? ' tab-bar__tab-shape--first' : ''}`

  if (totalW <= 0 || H <= 0) {
    return <div ref={ref} className={shapeClass} />
  }

  const tabR = totalW - CW // Right edge of flat top

  // Scale normalized coords to actual SVG coords
  const rx = (n: number) => tabR + n * CW
  const ry = (n: number) => n * H

  let path: string

  if (isFirstTab) {
    // First tab: no left S-curve, simple 12px rounded corner top-left, sharp bottom-left
    // Left edge at x=0 so the stroke only extends outward (no inner line visible)
    const L = 0
    const R = 12
    const K = 0.5522847 // Bezier approximation for quarter circle
    path = [
      `M ${L} ${H}`,
      `L ${L} ${R}`,
      `C ${L} ${R * (1 - K)} ${R * (1 - K)} 0 ${R} 0`,
      `L ${tabR} 0`,
      ...R_SEG.map(s =>
        `C ${rx(s.c1x)} ${ry(s.c1y)} ${rx(s.c2x)} ${ry(s.c2y)} ${rx(s.ex)} ${ry(s.ey)}`
      ),
    ].join(' ')
  } else {
    const lx = (n: number) => n * CW
    const ly = (n: number) => n * H
    path = [
      `M 0 ${H}`,
      ...L_SEG.map(s =>
        `C ${lx(s.c1x)} ${ly(s.c1y)} ${lx(s.c2x)} ${ly(s.c2y)} ${lx(s.ex)} ${ly(s.ey)}`
      ),
      `L ${tabR} 0`,
      ...R_SEG.map(s =>
        `C ${rx(s.c1x)} ${ry(s.c1y)} ${rx(s.c2x)} ${ry(s.c2y)} ${rx(s.ex)} ${ry(s.ey)}`
      ),
    ].join(' ')
  }

  const svgH = H + borderExt

  return (
    <div ref={ref} className={shapeClass}>
      <svg
        width={totalW}
        height={svgH}
        viewBox={`0 0 ${totalW} ${svgH}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d={path} className="tab-bar__tab-shape-path" />
        {borderExt > 0 && (
          <rect
            x={isFirstTab ? 1 : 3}
            y={H - 0.5}
            width={totalW - (isFirstTab ? 4 : 6)}
            height={borderExt + 0.5}
            className="tab-bar__tab-shape-cover"
          />
        )}
      </svg>
    </div>
  )
}

/**
 * SVG union shape for the active tab with S-curve edges from the Figma design.
 *
 * @description
 * Renders an absolutely-positioned SVG that draws the active tab's background
 * shape using 3-segment cubic bezier S-curves on each side. The shape matches
 * the Figma Union boolean design exactly.
 *
 * For the first tab in the bar (when a sidebar is open), the left S-curve is
 * replaced with a simple 12px rounded corner to avoid visual artifacts at the edge.
 *
 * On Windows, extends the fill 1px below to cover the content area's top border.
 *
 * Place inside a `position: relative` container that extends {@link CURVE_WIDTH}
 * beyond the tab on each side via CSS negative margins.
 *
 * @example
 * <div className="tab-bar__tab tab-bar__tab--active">
 *   <ActiveTabShape isFirstTab={index === 0} />
 *   <span className="tab-bar__tab-title">{title}</span>
 * </div>
 */
export const ActiveTabShape = React.memo(ActiveTabShapeComponent)
